'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { Message } from 'ably';
import { socketClient } from '@/app/clients/socket.client';

type SocketEventName = 'new' | 'changed' | 'completed';

type AblyContextType = {
  publish: <T = any>(
    eventName: SocketEventName,
    data: T,
    channelName?: string
  ) => Promise<void>;
  subscribe: <T = any>(
    eventName: SocketEventName,
    callback: (data: T, sameInstance: boolean) => void,
    channelName?: string
  ) => () => void;
};

const AblyContext = createContext<AblyContextType | null>(null);

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  const ably = useMemo(() => socketClient.getClient(), []);

  // Connection cleanup on refresh/tab close
  useEffect(() => {
    const handleUnload = () => {
      ably.close();
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      ably.close();
    };
  }, [ably]);

  const publish: AblyContextType['publish'] = useCallback(
    async (eventName, data, channelName = 'orders') => {
      if (ably.connection.state !== 'connected') {
        ably.connect();
      }
      const channel = ably.channels.get(channelName);
      await channel.publish(eventName, JSON.stringify(data));
    },
    [ably]
  );

  const subscribe: AblyContextType['subscribe'] = useCallback(
    (eventName, callback, channelName = 'orders') => {
      if (ably.connection.state !== 'connected') {
        ably.connect();
      }
      const channel = ably.channels.get(channelName);
      const handler = (msg: Message) => {
        callback(JSON.parse(msg.data), msg.connectionId === ably.connection.id);
      };

      channel.subscribe(eventName, handler);

      return () => {
        channel.unsubscribe(eventName, handler);
      };
    },
    [ably]
  );

  const value = useMemo(() => ({ publish, subscribe }), [publish, subscribe]);

  return <AblyContext.Provider value={value}>{children}</AblyContext.Provider>;
};

export const useSocket = () => {
  const ctx = useContext(AblyContext);
  if (!ctx) throw new Error('useSocket must be used inside <SocketProvider>');
  return ctx;
};
